<template>
  <div class="peg-board-container">
    <h2 class="peg-board-title">Patient's Pin Board</h2>
    <div class="peg-board-content">
        <!-- Loading overlay for patient switching -->
        <div v-if="isPegboardLoading" class="peg-board-loading">
          <Loading
            :loading="true"
            loadingText="Loading..."
            background=""
          />
        </div>
        <draggable
          v-if="!isPegboardLoading && cards && cards.length"
          v-model="cards"
          item-key="id"
          handle=".drag-handle"
          animation="600"
          @end="onDragEnd"
        >
          <template #item="{ element }">
            <div class="peg-card-wrapper">
              <component
                :is="element.component"
                :patient="element.component === 'ContactCard' || element.component === 'DemographicsCard' ? (roomId || currentPatientId) : currentPatientId"
                :patient-id="currentPatientId"
                :appointment="element.appointment"
                :cardData="element.cardData"
                :context="'pegboard'"
                :medications="element.component === 'MedicationCard' ? allMedications : undefined"
                :allergies="element.component === 'AllergyCard' ? allergies : undefined"
                :billing-orders="element.component === 'BillingOrderCard' ? billingOrders : undefined"
                :health-info="element.component === 'HealthInfoCard' ? healthInfo : undefined"
                :documents="element.component === 'DocumentCard' ? documents : undefined"
                :isLoading="element.component === 'MedicationCard' ? isLoadingMedications : element.component === 'HealthInfoCard' ? isLoadingHealthInfo : element.component === 'BillingOrderCard' ? isLoadingBillingOrders : element.component === 'DocumentCard' ? isLoadingDocuments : false"
                :showFooter="true"
                @unpin-card="handleUnpinCard"
                @add-new="() => handleAddNew(element)"
                @edit="(data) => handleEdit(data, element)"
                @edit-note="handleEditNote"
                @edit-contact="handleEditContact"
                @edit-appointment="(appointment) => handleEditAppointment(appointment, element)"
                @edit-task="(task) => handleEditTask(task, element)"
                @discontinue="(data) => handleDiscontinue(data, element)"
                @reconcile="handleReconcile"
              />
            </div>
          </template>
        </draggable>

        <div v-else-if="!isPegboardLoading" class="peg-board-empty">
          <div class="peg-board-empty-inner">
            <div class="peg-board-empty-icon" aria-hidden="true">
              <!-- Pin icon -->
              <svg xmlns="http://www.w3.org/2000/svg" width="89" height="88" viewBox="0 0 89 88" fill="none">
                <path d="M40.333 30.0747V36.978H34.8115V45.6118L34.876 45.7271L40.333 55.3706V62.7104H27.3896V77.2534L23.958 80.7036L20.4873 77.2524V62.7104H7.58789V55.3726L13.0449 45.7271L13.1104 45.6118V36.978H7.58789V30.0747H40.333ZM15.4072 14.4214H73.5703C75.747 14.4214 77.5872 15.1824 79.1191 16.7144C80.651 18.2463 81.4121 20.0865 81.4121 22.2632V65.7368C81.4121 67.9135 80.651 69.7537 79.1191 71.2856C77.5872 72.8176 75.747 73.5786 73.5703 73.5786H45.4375V66.2593H74.0918V21.7407H7.60352C7.71326 19.7958 8.46912 18.1273 9.88477 16.7144C11.4208 15.1812 13.2527 14.4215 15.4072 14.4214ZM20.0137 47.604L15.7021 55.0571L15.2686 55.8071H32.6523L32.2188 55.0571L27.9072 47.6021V37.394H20.0137V47.604Z" fill="#F2F2F2" stroke="#255F90"/>
              </svg>
            </div>
            <p class="peg-board-empty-text" :class="isLeftColumnCollapsed ? 'expanded-text' : 'collapsed-text'">You can pin cards from the tabs here.</p>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import ContactCard from '../../components/pegboard-cards/ContactCard.vue';
import DemographicsCard from '../../components/pegboard-cards/DemographicsCard.vue';
import AppointmentCard from '../../components/pegboard-cards/AppointmentCard.vue';
import AssignedQuestionnaireCard from '../../components/pegboard-cards/AssignedQuestionnaireCard.vue';
import TaskCard from '../../components/pegboard-cards/TaskCard.vue';
import FormCard from '../../components/pegboard-cards/FormCard.vue';
import BillingCard from '../../components/pegboard-cards/BillingCard.vue';
import BillingOrderCard from '../../components/pegboard-cards/BillingOrderCard.vue';
import MedicationCard from '../../components/pegboard-cards/MedicationCard.vue';
import SampleCard from '../../components/pegboard-cards/SampleCard.vue';
import AllergyCard from '../../components/pegboard-cards/AllergyCard.vue';
import HealthInfoCard from '../../components/pegboard-cards/HealthInfoCard.vue';
import NotesCard from '../../components/pegboard-cards/NotesCard.vue';
import OrderHandlingCard from '../../components/pegboard-cards/OrderHandlingCard.vue';
import InternalNotesCard from '../../components/pegboard-cards/InternalNotesCard.vue';
import DocumentCard from '../../components/pegboard-cards/DocumentCard.vue';
import draggable from "vuedraggable"
import Loading from '@/NewLibrary/Loading/Loading.vue'
import { mapGetters, mapActions } from 'vuex';
import { useMyPatientsStore } from '@/store/helpers';

export default {
  name: 'Pegboard',
  components: {
    draggable,
    Loading,
    ContactCard,
    DemographicsCard,
    AppointmentCard,
    AssignedQuestionnaireCard,
    TaskCard,
    FormCard,
    BillingCard,
    BillingOrderCard,
    MedicationCard,
    SampleCard,
    AllergyCard,
    HealthInfoCard,
    NotesCard,
    OrderHandlingCard,
    InternalNotesCard,
    DocumentCard
  },
  props: {
    patient: {
      type: String,
      required: ''
    },
    roomId: {
      type: String,
      default: null
    }, 
    isLeftColumnCollapsed: {
      type: Boolean,
      required: true
    }
  },
  emits: ['add-new', 'edit', 'edit-note', 'edit-contact', 'edit-appointment', 'edit-task', 'discontinue', 'reconcile'],
  setup(props) {
    const { patientInfoStore } = useMyPatientsStore()
    // Get all appointments for multiple cards
    const appointments = computed(() => {
      return patientInfoStore.getters.getAllAppointments || []
    })
    
    // Get pegboard cards from store
    const pegboardCards = computed(() => {
      return patientInfoStore.getters.getPegboardCards.value || []
    })
    
    // Get shared medications and allergies from store
    const allMedications = computed(() => {
      return patientInfoStore.state.currentPatientMedications.value || []
    })
    const allergies = computed(() => {
      return patientInfoStore.state.currentPatientAllergies.value || []
    })
    const healthInfo = computed(() => {
      return patientInfoStore.state.currentPatientHealthInfo?.value || null
    })
    const billingOrders = computed(() => {
      return patientInfoStore.state.currentPatientBillingOrders.value || []
    })
    const isLoadingMedications = computed(() => {
      return patientInfoStore.state.medicationsLoading.value || false
    })
    const isLoadingHealthInfo = computed(() => {
      return patientInfoStore.state.healthInfoLoading.value || false
    })
    const isLoadingBillingOrders = computed(() => {
      return patientInfoStore.state.billingOrdersLoading.value || false
    })
    const documents = computed(() => {
      return patientInfoStore.state.currentPatientDocuments.value || []
    })
    const isLoadingDocuments = computed(() => {
      return patientInfoStore.state.documentsLoading.value || false
    })
    const isPegboardLoading = computed(() => {
      return patientInfoStore.state.pegboardLoading.value || false
    })
    return {
      appointments,
      pegboardCards,
      allMedications,
      allergies,
      healthInfo,
      billingOrders,
      documents,
      isLoadingMedications,
      isLoadingHealthInfo,
      isLoadingBillingOrders,
      isLoadingDocuments,
      isPegboardLoading
    }
  },
  data() {
    return {
      expanded: {
        contact: false,
        demographics: true,
        appointment: true,
        questionnaire: false,
        task: true,
        form: true,
        billing: true,
        medication: true,
      },
      cards: []
    };
  },
  computed: {
    ...mapGetters('myPatients/rooms', ['getPatientByRoomId']),
    
    currentPatientId() {
      // Get patient ID from the current patient data
      if (this.roomId) {
        const p = this.getPatientByRoomId(this.roomId);
        if (p?.patientBasicInfo?.patientId) {
          return p.patientBasicInfo.patientId;
        }
      }
      // Fallback to prop patient if roomId is not provided or doesn't resolve to a patient
      return this.patient || null;
    },
    
  },
  watch: {
    appointments: {
      handler(newAppointments) {
        console.log('📅 Appointments changed, reinitializing cards:', newAppointments);
        this.initializeCards()
      },
      deep: true
    },
    pegboardCards: {
      handler(newCards) {
        this.initializeCards()
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    toggleCard(key) {
      if (typeof this.expanded[key] !== 'undefined') {
        this.expanded[key] = !this.expanded[key];
      }
    },
    
    async onDragEnd(event) {
      if (event.oldIndex !== event.newIndex) {
        try {
          const newCardOrder = this.cards.map(card => card.cardData);
          const newCardOrderIds = this.cards.map(card => card.id);
          await this.$store.dispatch('myPatients/patientInfo/updatePegboardCardsOrder', {
            customer_id: this.$store.state.login.userInfo.customer_id,
            clinic_id: this.$store.state.login.userInfo.clinic_id,
            patient_id: this.currentPatientId,
            newCardOrder: newCardOrder,
            newCardOrderIds: newCardOrderIds,
          });
        } catch (error) {
          console.error('Failed to update card order:', error);
        }
      }
    },

    async handleUnpinCard(cardData) {
      try {
        const card_id = typeof cardData === 'string' ? cardData : cardData.id;
        await this.$store.dispatch('myPatients/patientInfo/unpinCard', {
          customer_id: this.$store.state.login.userInfo.customer_id,
          clinic_id: this.$store.state.login.userInfo.clinic_id,
          patient_id: this.currentPatientId,
          card_id: card_id,
        });
      } catch (error) {
        console.error('Failed to unpin card:', error);
      }
    },

    handleAddNew(element) {
      const entityType = element.cardData?.entity_type || 'medication';
      this.$emit('add-new', { entityType });
    },

    handleEdit(data, element) {
      const entityType = element.cardData?.entity_type || 'medication';
      this.$emit('edit', { data, entityType });
    },

    handleDiscontinue(data, element) {
      const entityType = element.cardData?.entity_type || 'medication';
      this.$emit('discontinue', { data, entityType });
    },

    handleReconcile() {
      this.$emit('reconcile');
    },

    handleEditNote(noteId) {
      this.$emit('edit-note', noteId);
    },

    handleEditContact() {
      this.$emit('edit-contact');
    },

    handleEditAppointment(appointment, element) {
      this.$emit('edit-appointment', appointment);
    },

    handleEditTask(task, element) {
      this.$emit('edit-task', task);
    },

    initializeCards() {
      
      // Map pegboard cards to Vue components based on category and entity_type
      const mappedCards = this.pegboardCards.map(card => {
        let component = null;
        let cardData = null;

        // Map based on category and entity_type
        if (card.category === 'basicInfo' && card.entity_type === 'contact') {
          component = 'ContactCard';
        } else if (card.category === 'basicInfo' && card.entity_type === 'demographics') {
          component = 'DemographicsCard';
        } else if (card.category === 'Calendar' && card.entity_type === 'appointment') {
          component = 'AppointmentCard';
          // AppointmentCard will fetch data from store using entity_id from cardData
        } else if (card.category === 'Calendar' && card.entity_type === 'task') {
          component = 'TaskCard';
        } else if (card.category === 'Calendar' && card.entity_type === 'form') {
          component = 'FormCard';
        } else if (card.category === 'Calendar' && card.entity_type === 'questionnaire') {
          component = 'AssignedQuestionnaireCard';
        } else if (card.category === 'Billing' && card.entity_type === 'invoice') {
          // component = 'BillingCard';
        } else if (card.category === 'Billing' && card.entity_type === 'order') {
          component = 'BillingOrderCard';
        } else if (card.category === 'Medical_Record' && card.entity_type === 'medication') {
          component = 'MedicationCard';
        } else if (card.category === 'Billing' && card.entity_type === 'sample') {
          component = 'SampleCard';
        } else if (card.category === 'Medical_Record' && card.entity_type === 'allergy') {
          component = 'AllergyCard';
        } else if (card.category === 'Medical_Record' && card.entity_type === 'health') {
          component = 'HealthInfoCard';
        } else if (card.category === 'Medical_Record' && card.entity_type === 'notes') {
          // Encounter Notes card temporarily hidden
          // component = 'NotesCard';
        } else if (card.category === 'basicInfo' && card.entity_type === 'handling') {
          component = 'OrderHandlingCard';
        } else if (card.category === 'internalNotes' && card.entity_type === 'internalNote') {
          component = 'InternalNotesCard';
        } else if (card.category === 'Medical_Record' && card.entity_type === 'document') {
          component = 'DocumentCard';
        }

        // Only return cards that have a valid component mapping
        if (component) {
          return {
            id: card.id,
            component: component,
            cardData: card
          };
        }
        return null;
      }).filter(card => card !== null);

      this.cards = mappedCards;
    }
  },
};
</script>

<style scoped>

.peg-board-container {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.collapsed-text {
  width: 180px;
}

.expanded-text {
  width: 100%;
}
.peg-card-wrapper {
  margin-top: 6px;
  margin-bottom: 6px;
}
.drag-handle {
  user-select: none;
}
.peg-board-title {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 22px;
  color: #ffffff;
  text-align: center;
}

.peg-board-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.26) transparent;
}

.peg-board-content::-webkit-scrollbar {
  width: 6px;
}

.peg-board-content::-webkit-scrollbar-track {
  background: transparent;
}

.peg-board-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.26);
  border-radius: 999px;
}

.peg-board-content:hover::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.38);
}

/* Empty state container (outer rounded panel) */
.peg-board-empty {
  background: #004879;
  border-radius: 10px;
  flex: 1; /* fill remaining space */
  display: flex;
}

/* Inner lighter panel */
.peg-board-empty-inner {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  flex: 1; /* take all space inside */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.peg-board-empty-icon {
  margin-bottom: 16px;
}

.peg-board-empty-text {
  color: #FFFFFF;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
}

.info-card {
  background-color: #f9fbfd;
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 25px 0px rgba(74, 74, 74, 0.15);
}

/* Override item backgrounds to match pegboard card background */
:deep(.allergy-item),
:deep(.medication-item) {
  background-color: #f9fbfd !important;
}

.info-card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.info-card-content-col-group {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
}

.info-row-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-grow: 1;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-container {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-style {
  width: 20px;
  height: 20px;
}

.info-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.action-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.appointment-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 50px;
  height: 64px;
  border-radius: 5px;
  background-color: #ffffff;
  box-shadow: 0px 1px 15px 0px rgba(74, 74, 74, 0.1);
  overflow: hidden;
}

.month {
  background-color: #004879;
  color: white;
  width: 100%;
  text-align: center;
  padding: 2px 0;
  font-size: 14px;
}

.day {
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
  font-family: Lato;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 120% */
  text-transform: capitalize;
  height: 24px;
}

.weekday {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1;
  padding-bottom: 3px;
}

.appointment-details {
    flex-grow: 1;
}

.detail-title {
    font-size: 12px;
    color: #666;
}
.detail-text {
    font-size: 14px;
    color: #333;
    margin-bottom: 4px;
}
.action-icons-appointment {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 20px;
}
.info-text-title {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #666;
}
.info-text-large {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #333;
    font-weight: 500;
}

/* Badge for billing preference */
.badge {
  color: #25B8C2;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 120% */
  letter-spacing: 0.25px;
  display: flex;
  padding: 4px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.badge-default {
  border-radius: 20px;
  background: #EBFFF8;
  width: 68px;
}

/* Expanded card footer visibility */
.card-expand {
  display: none;
}

.info-card.expanded:hover .card-expand {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-top: 10px;
  margin-top: 10px;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
}

.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

/* Pegboard loading overlay */
.peg-board-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}
</style>
